/**
 * Keycloak service for authentication and token management
 */
import Keycloak from "keycloak-js";
import getKeycloakConfig from "./keycloakConfig";

class KeycloakService {
    static instance = null;
    static initialized = false;
    static initPromise = null;

    static getInstance() {
        if (!KeycloakService.instance) {
            KeycloakService.instance = new Keycloak(getKeycloakConfig());
        }
        return KeycloakService.instance;
    }

    static async init() {
        // Prevent multiple initialization - return existing promise if init in progress
        if (KeycloakService.initPromise) {
            console.log("Keycloak init already in progress, waiting...");
            return KeycloakService.initPromise;
        }

        // Return cached result if already initialized
        if (KeycloakService.initialized) {
            console.log("Keycloak already initialized");
            const keycloak = KeycloakService.getInstance();
            return { authenticated: keycloak.authenticated, keycloak };
        }

        KeycloakService.initPromise = KeycloakService._doInit();
        return KeycloakService.initPromise;
    }

    static async _doInit() {
        const keycloak = KeycloakService.getInstance();

        console.log("Keycloak init starting...");
        console.log("Origin:", window.location.origin);

        // Build init options - skip silent SSO on deployed environments (causes iframe timeout)
        const initOptions = {
            onLoad: "check-sso",
            pkceMethod: "S256",
            checkLoginIframe: false,
            enableLogging: true,
            flow: "standard",
            messageReceiveTimeout: 10000,
        };

        // Only use silent SSO on localhost
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
            initOptions.silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso.html`;
            console.log("Silent SSO URI:", initOptions.silentCheckSsoRedirectUri);
        } else {
            console.log("Skipping silent SSO check on deployed environment");
        }

        try {
            const authenticated = await keycloak.init(initOptions);
            console.log("Keycloak initialized successfully, authenticated:", authenticated);
            KeycloakService.initialized = true;
            return { authenticated, keycloak };
        } catch (error) {
            console.error("Keycloak initialization error:", error);
            console.error("Error message:", error?.message);
            KeycloakService.initialized = true; // Mark as initialized even on error to prevent retries
            return { authenticated: false, keycloak, error };
        }
    }

    static login() {
        const keycloak = KeycloakService.getInstance();
        const cleanRedirectUri = window.location.origin + window.location.pathname + "?sso=true";
        keycloak.login({ redirectUri: cleanRedirectUri });
    }

    static logout() {
        const keycloak = KeycloakService.getInstance();
        keycloak.logout({ redirectUri: window.location.origin });
    }

    static getToken() {
        const keycloak = KeycloakService.getInstance();
        return keycloak.token;
    }

    static isTokenExpired(minValidity = 0) {
        const keycloak = KeycloakService.getInstance();
        return keycloak.isTokenExpired(minValidity);
    }

    static updateToken(minValidity = 0) {
        const keycloak = KeycloakService.getInstance();
        return keycloak.updateToken(minValidity);
    }

    static getProfile() {
        const keycloak = KeycloakService.getInstance();
        return keycloak.profile || null;
    }

    static isAuthenticated() {
        const keycloak = KeycloakService.getInstance();
        return keycloak.authenticated;
    }

    /**
     * Refresh token if it will expire within minValidity seconds
     * @param {number} minValidity - Minimum validity in seconds (default 30)
     * @returns {Promise<string|null>} - Returns new token or null if refresh failed
     */
    static async refreshTokenIfNeeded(minValidity = 30) {
        const keycloak = KeycloakService.getInstance();
        if (!keycloak.authenticated) {
            return null;
        }
        try {
            const refreshed = await keycloak.updateToken(minValidity);
            if (refreshed) {
                console.log("Keycloak token refreshed");
                localStorage.setItem("kc_token", keycloak.token);
            }
            return keycloak.token;
        } catch (error) {
            console.error("Token refresh failed:", error);
            // Clear stored token and trigger re-login
            localStorage.removeItem("kc_token");
            KeycloakService.login();
            return null;
        }
    }

    /**
     * Get a valid token, refreshing if necessary
     * @returns {Promise<string|null>} - Returns valid token or null
     */
    static async getValidToken() {
        const keycloak = KeycloakService.getInstance();
        if (!keycloak.authenticated) {
            return null;
        }
        // Refresh if token expires within 30 seconds
        if (keycloak.isTokenExpired(30)) {
            return await KeycloakService.refreshTokenIfNeeded(30);
        }
        return keycloak.token;
    }
}

export default KeycloakService;
